import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../constants/AuthContext';
import { loginUser, forgotPassword, resetPassword } from '../constants/api';
import { COLORS } from '../constants/theme';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState('login'); // login | forgot | otp | reset
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const isMobile = window.innerWidth < 768;

  // Forgot password state
  const [fpEmail, setFpEmail] = useState('');
  const [fpOtp, setFpOtp] = useState(['', '', '', '', '', '']);
  const [fpNewPassword, setFpNewPassword] = useState('');
  const [fpConfirmPassword, setFpConfirmPassword] = useState('');
  const [showFpPassword, setShowFpPassword] = useState(false);
  const otpRefs = Array.from({ length: 6 }, () => React.createRef());

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password.trim()) return setError('Please fill in all fields.');
    setLoading(true);
    setError('');
    try {
      const res = await loginUser({ identifier: identifier.trim(), password });
      login(res.data);
      navigate('/dashboard');
    } catch (err) {
      const msg = err?.response?.data ?? 'Invalid username or password.';
      setError(typeof msg === 'string' ? msg : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSend = async (e) => {
    e.preventDefault();
    if (!fpEmail.trim() || !fpEmail.includes('@')) return setError('Please enter a valid email address.');
    setLoading(true);
    setError('');
    try {
      await forgotPassword({ email: fpEmail.trim() });
      setMode('otp');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (val, index) => {
    const cleaned = val.replace(/[^0-9]/g, '');
    const newOtp = [...fpOtp];
    newOtp[index] = cleaned;
    setFpOtp(newOtp);
    if (cleaned && index < 5) otpRefs[index + 1]?.current?.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !fpOtp[index] && index > 0) {
      otpRefs[index - 1]?.current?.focus();
    }
  };

  const handleOtpContinue = (e) => {
    e.preventDefault();
    if (fpOtp.join('').length !== 6) return setError('Please enter the full 6-digit code.');
    setError('');
    setMode('reset');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (fpNewPassword.length < 8) return setError('Password must be at least 8 characters.');
    if (fpNewPassword !== fpConfirmPassword) return setError('Passwords do not match.');
    setLoading(true);
    setError('');
    try {
      await resetPassword({ email: fpEmail.trim(), otp: fpOtp.join(''), newPassword: fpNewPassword });
      setMode('login');
      setError('');
      alert('Password reset successful. You can now login.');
    } catch (err) {
      const msg = err?.response?.data ?? 'Invalid or expired code.';
      setError(typeof msg === 'string' ? msg : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '13px 14px',
    borderRadius: 10,
    border: `1.5px solid ${COLORS.border}`,
    fontSize: 14,
    color: COLORS.ink,
    background: COLORS.bg,
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const renderForm = () => {
    if (mode === 'forgot') return (
      <div>
        <button onClick={() => { setMode('login'); setError(''); }} style={{ background: 'none', border: 'none', color: COLORS.accent, fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 24, padding: 0, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Back to login
        </button>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: COLORS.ink, marginBottom: 8 }}>Forgot Password?</div>
          <div style={{ fontSize: 14, color: COLORS.inkLight }}>Enter your email and we will send you a reset code.</div>
        </div>
        <form onSubmit={handleForgotSend} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Email Address</label>
            <input type="email" value={fpEmail} onChange={e => setFpEmail(e.target.value)} placeholder="Enter your email address" style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />
          </div>
          {error && <div style={{ background: '#FEE2E2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: COLORS.danger, fontWeight: 600 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ background: COLORS.accent, color: 'white', border: 'none', borderRadius: 10, padding: '14px', fontSize: 15, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: 'inherit', width: '100%' }}>
            {loading ? 'Sending...' : 'Send Reset Code'}
          </button>
        </form>
      </div>
    );

    if (mode === 'otp') return (
      <div>
        <button onClick={() => { setMode('forgot'); setError(''); }} style={{ background: 'none', border: 'none', color: COLORS.accent, fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 24, padding: 0, fontFamily: 'inherit' }}>
          ← Back
        </button>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: COLORS.ink, marginBottom: 8 }}>Enter Reset Code</div>
          <div style={{ fontSize: 14, color: COLORS.inkLight, marginBottom: 4 }}>We sent a 6-digit code to</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.accent }}>{fpEmail}</div>
        </div>
        <form onSubmit={handleOtpContinue} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            {fpOtp.map((digit, index) => (
              <input
                key={index}
                ref={otpRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleOtpChange(e.target.value, index)}
                onKeyDown={e => handleOtpKeyDown(e, index)}
                autoFocus={index === 0}
                style={{ width: 48, height: 56, borderRadius: 10, border: `2px solid ${digit ? COLORS.accent : COLORS.border}`, fontSize: 22, fontWeight: 800, textAlign: 'center', color: COLORS.ink, background: digit ? COLORS.accentLight : COLORS.bg, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
            ))}
          </div>
          {error && <div style={{ background: '#FEE2E2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: COLORS.danger, fontWeight: 600 }}>{error}</div>}
          <button type="submit" disabled={fpOtp.join('').length !== 6} style={{ background: COLORS.accent, color: 'white', border: 'none', borderRadius: 10, padding: '14px', fontSize: 15, fontWeight: 800, cursor: 'pointer', opacity: fpOtp.join('').length !== 6 ? 0.6 : 1, fontFamily: 'inherit', width: '100%' }}>
            Continue
          </button>
        </form>
      </div>
    );

    if (mode === 'reset') return (
      <div>
        <button onClick={() => { setMode('otp'); setError(''); }} style={{ background: 'none', border: 'none', color: COLORS.accent, fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 24, padding: 0, fontFamily: 'inherit' }}>
          ← Back
        </button>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: COLORS.ink, marginBottom: 8 }}>Set New Password</div>
          <div style={{ fontSize: 14, color: COLORS.inkLight }}>Create a new password for your account.</div>
        </div>
        <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>New Password</label>
            <div style={{ position: 'relative' }}>
              <input type={showFpPassword ? 'text' : 'password'} value={fpNewPassword} onChange={e => setFpNewPassword(e.target.value)} placeholder="Enter new password"
                style={{ ...inputStyle, paddingRight: 44 }}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
              <button type="button" onClick={() => setShowFpPassword(v => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0 }}>
                {showFpPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Confirm Password</label>
            <input type={showFpPassword ? 'text' : 'password'} value={fpConfirmPassword} onChange={e => setFpConfirmPassword(e.target.value)} placeholder="Confirm new password"
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />
          </div>
          {error && <div style={{ background: '#FEE2E2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: COLORS.danger, fontWeight: 600 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ background: COLORS.accent, color: 'white', border: 'none', borderRadius: 10, padding: '14px', fontSize: 15, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, fontFamily: 'inherit', width: '100%' }}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    );

    return (
      <div>
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 24, fontWeight: 900, color: COLORS.ink, marginBottom: 8, letterSpacing: -0.5 }}>Welcome back</div>
          <div style={{ fontSize: 14, color: COLORS.inkLight }}>Login to manage your business</div>
        </div>
        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Username or Email</label>
            <input type="text" value={identifier} onChange={e => setIdentifier(e.target.value)} placeholder="Enter your username or email" style={inputStyle}
              onFocus={e => e.target.style.borderColor = COLORS.accent}
              onBlur={e => e.target.style.borderColor = COLORS.border}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, display: 'block', marginBottom: 6 }}>Password</label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password"
                style={{ ...inputStyle, paddingRight: 44 }}
                onFocus={e => e.target.style.borderColor = COLORS.accent}
                onBlur={e => e.target.style.borderColor = COLORS.border}
              />
              <button type="button" onClick={() => setShowPassword(v => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0 }}>
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <button type="button" onClick={() => { setMode('forgot'); setError(''); }} style={{ background: 'none', border: 'none', color: COLORS.accent, fontWeight: 700, fontSize: 13, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
              Forgot Password?
            </button>
          </div>
          {error && <div style={{ background: '#FEE2E2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: COLORS.danger, fontWeight: 600 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ background: COLORS.accent, color: 'white', border: 'none', borderRadius: 10, padding: '14px', fontSize: 15, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, marginTop: 8, fontFamily: 'inherit', width: '100%' }}>
            {loading ? 'Logging in...' : 'Login →'}
          </button>
        </form>
        <div style={{ marginTop: 24, textAlign: 'center', fontSize: 14, color: COLORS.inkLight }}>
          Don't have an account?{' '}
          <Link to="/register" style={{ color: COLORS.accent, fontWeight: 700, textDecoration: 'none' }}>Create one</Link>
        </div>
      </div>
    );
  };

  return (
    <div style={{ height: '100vh', background: 'white', display: 'flex', overflow: 'hidden' }}>

      {/* Left branding — desktop only */}
      {!isMobile && (
        <div style={{ flex: 1, background: COLORS.ink, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '60px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(61,170,106,0.15) 0%, transparent 70%)' }} />
          <div style={{ position: 'absolute', bottom: -80, left: -80, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(26,107,60,0.2) 0%, transparent 70%)' }} />
          <div style={{ position: 'relative', zIndex: 2 }}>
            <div style={{ fontFamily: 'sans-serif', fontSize: 32, fontWeight: 900, marginBottom: 16 }}>
              <span style={{ color: 'white' }}>Stok</span>
              <span style={{ color: '#3DAA6A' }}>Pilot</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: 'white', lineHeight: 1.2, marginBottom: 16, letterSpacing: -0.5 }}>
              Run your business<br />with total clarity.
            </div>
            <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, maxWidth: 340 }}>
              Track your stock, record every sale and know your profit in real time. Built for Nigerian business owners.
            </div>
            <div style={{ display: 'flex', gap: 32, marginTop: 48, paddingTop: 32, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              {[['100%', 'Free to start'], ['2 min', 'Setup time'], ['24/7', 'Always synced']].map(([num, label]) => (
                <div key={label}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: 'white' }}>{num}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Right form */}
      <div style={{ width: isMobile ? '100%' : 480, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '40px 24px' : '48px 56px', background: 'white', overflowY: 'auto' }}>
        <div style={{ width: '100%', maxWidth: 360 }}>

          {/* Mobile logo */}
          {isMobile && (
            <div style={{ textAlign: 'center', marginBottom: 32 }}>
              <div style={{ fontFamily: 'sans-serif', fontSize: 36, fontWeight: 900, marginBottom: 8 }}>
                <span style={{ color: COLORS.ink }}>Stok</span>
                <span style={{ color: COLORS.accent }}>Pilot</span>
              </div>
              <div style={{ fontSize: 13, color: COLORS.inkLight }}>Track Stock. Record Sales. Know Your Profit.</div>
            </div>
          )}

          {renderForm()}
        </div>
      </div>
    </div>
  );
}